import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, X, Plus, Minus } from "lucide-react";
import { SutraMark } from "@/components/sutra/SutraMark";
import { track } from "@/lib/analytics";

/**
 * Friend Toolkit — a grid of common "what people ask about Hinduism"
 * questions. Each card expands inline to reveal a short, confident,
 * friendly "say this" answer plus Copy + Share actions.
 *
 * Copy: writes the answer to the clipboard (with a small toast).
 * Share: opens a clean, screenshot-friendly quote card (question +
 * answer + Sutra mark) for users to send in iMessage / WhatsApp /
 * Instagram. Native Web Share is offered when available; otherwise
 * the modal acts as a styled fallback with a Copy button.
 */

interface Card {
  id: string;
  question: string;
  answer: string;
}

const CARDS: Card[] = [
  {
    id: "many-gods",
    question: "Why do you have so many gods?",
    answer:
      "One reality, many forms — like one person is a child, a friend, and a boss. Not many gods competing; one, met in the shape each person needs.",
  },
  {
    id: "statues",
    question: "Why pray to statues?",
    answer:
      "The image is a focus point, not the god — like a photo of someone you love. The form just helps a wandering mind concentrate.",
  },
  {
    id: "way-of-life",
    question: "Is it even a religion, or a 'way of life'?",
    answer:
      "Both. 'Religion' is a Western frame; 'dharma' is closer to 'the way things are meant to be lived.' Less a rulebook, more a way of living.",
  },
  {
    id: "elephant-monkey",
    question: "What's with the elephant god / monkey god?",
    answer:
      "They're forms with meaning — Ganesha's elephant head = wisdom and removing obstacles; Hanuman = devotion and courage. Symbols, not literal zoology.",
  },
  {
    id: "karma",
    question: "What does karma actually mean?",
    answer:
      "Not cosmic punishment — it starts with intention. Your actions have consequences, beginning with why you do them.",
  },
  {
    id: "dharma",
    question: "What is dharma?",
    answer:
      "Your right way of acting — doing what's yours to do, and doing it well, while holding the outcome lightly.",
  },
];

export const FriendToolkit = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [shareCard, setShareCard] = useState<Card | null>(null);

  const copyAnswer = async (answer: string, question: string) => {
    track("toolkit_copy", { question });
    try {
      await navigator.clipboard.writeText(answer);
      toast("Copied", { description: "Paste it wherever you need." });
    } catch {
      toast.error("Couldn't copy", {
        description: "Long-press to copy manually.",
      });
    }
  };

  return (
    <>
      <ul
        role="list"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {CARDS.map((card) => {
          const isOpen = openId === card.id;
          return (
            <li key={card.id}>
              <ToolkitCard
                card={card}
                isOpen={isOpen}
                onToggle={() => {
                  const next = isOpen ? null : card.id;
                  if (next) track("toolkit_card_opened", { question: card.question });
                  setOpenId(next);
                }}
                onCopy={() => copyAnswer(card.answer, card.question)}
                onShare={() => {
                  track("toolkit_share", { question: card.question });
                  setShareCard(card);
                }}
              />
            </li>
          );
        })}
      </ul>

      {shareCard && (
        <ShareCardModal
          card={shareCard}
          onClose={() => setShareCard(null)}
          onCopy={() =>
            copyAnswer(`"${shareCard.question}"\n\n${shareCard.answer}\n\n— Sutra`, shareCard.question)
          }
        />
      )}
    </>
  );
};

interface ToolkitCardProps {
  card: Card;
  isOpen: boolean;
  onToggle: () => void;
  onCopy: () => void;
  onShare: () => void;
}

const ToolkitCard = ({
  card,
  isOpen,
  onToggle,
  onCopy,
  onShare,
}: ToolkitCardProps) => {
  const panelId = `toolkit-panel-${card.id}`;
  const btnId = `toolkit-btn-${card.id}`;

  return (
    <div
      className={`group flex h-full flex-col rounded-2xl border border-border/70 bg-paper-warm/60 shadow-sm transition-all duration-300 ${
        isOpen
          ? "border-primary/40 shadow-md ring-1 ring-primary/15"
          : "hover:border-primary/30 hover:shadow-md"
      }`}
    >
      <button
        id={btnId}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-3 rounded-2xl px-5 pt-5 pb-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <span className="font-display text-lg font-medium leading-snug text-foreground">
          {card.question}
        </span>
        <span
          aria-hidden="true"
          className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border/70 text-primary transition-transform duration-300 ${
            isOpen ? "rotate-180 bg-primary/10" : "bg-background"
          }`}
        >
          {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        </span>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        hidden={!isOpen}
        className="px-5 pb-5"
      >
        <p className="border-t border-border/60 pt-4 text-[15px] leading-relaxed text-ink-soft">
          {card.answer}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-3.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy text
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share card
          </button>
        </div>
      </div>
    </div>
  );
};

interface ShareCardModalProps {
  card: Card;
  onClose: () => void;
  onCopy: () => void;
}

const ShareCardModal = ({ card, onClose, onCopy }: ShareCardModalProps) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: "Sutra",
        text: `"${card.question}"\n\n${card.answer}\n\n— Sutra`,
      });
    } catch {
      /* user cancelled or unsupported — no-op */
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Shareable card"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 px-4 py-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-background text-foreground shadow-md ring-1 ring-border/70 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          <X className="h-4 w-4" />
        </button>

        {/* The visual card itself — screenshot-friendly */}
        <div className="overflow-hidden rounded-3xl bg-paper-warm shadow-xl ring-1 ring-border/60">
          <div className="relative px-7 py-9 sm:px-9 sm:py-11">
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-transparent to-accent/[0.05]"
            />
            <div className="relative">
              <p className="text-[11px] uppercase tracking-[0.22em] text-primary/80">
                When a friend asks…
              </p>
              <p className="mt-4 font-display text-2xl font-light leading-snug text-foreground sm:text-[26px]">
                "{card.question}"
              </p>
              <div className="my-5 h-px w-10 bg-primary/40" />
              <p className="text-[15px] leading-relaxed text-ink-soft">
                {card.answer}
              </p>
              <div className="mt-8 flex items-center gap-2">
                <SutraMark idSuffix={`share-${card.id}`} className="h-5 w-5" />
                <span className="font-display text-sm font-medium tracking-tight text-foreground">
                  Sutra
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {canNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share…
            </button>
          )}
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-4 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy text
          </button>
        </div>
        <p className="mt-3 text-center text-[11px] text-ink-soft/80">
          Screenshot this card to send it anywhere.
        </p>
      </div>
    </div>
  );
};

export default FriendToolkit;
